import React from 'react';
import { nombre } from './format';
import './VerdictPrix.css';

/**
 * LE verdict de prix, à quatre niveaux (2026-09-15) — un seul composant pour le
 * vérificateur, la fiche annonce et les cartes.
 *
 *   SOLIDE       cote de comparables fiable          tuile pleine
 *   INDICATIVE   cote de comparables peu nombreux    tuile en pointillés, « ≈ »
 *   PERCENTILE   rang parmi les voitures en vente    réglette, AUCUN prix
 *   ESTIMATION   régression + fourchette à 80 %      barre de fourchette, rayée
 *   (rien)       silence expliqué
 *
 * Chaque niveau a sa forme propre : on ne doit jamais pouvoir prendre une
 * estimation pour une cote. Le vert ne sert qu'à un écart d'ARGENT mesuré sur
 * des comparables (niveaux 1-2) ; rang et fourchette restent en bleu.
 *
 * `v` : { niveau, prix, cote, ecart_eur, ecart_pct, comparables, percentile,
 *         groupe_taille, groupe, estimation, bas, haut, explication }
 */
const ZONE_MORTE_PCT = 3;

export const LIBELLES_NIVEAUX = {
  SOLIDE: 'Cote solide',
  INDICATIVE: 'Cote indicative',
  PERCENTILE: 'Positionnement',
  ESTIMATION: 'Estimation indicative',
};

const POURQUOI = {
  SOLIDE: 'Médiane des prix de voitures identiques en vente (même modèle, motorisation, année à un an près, kilométrage proche), écartée des annonces aberrantes. Erreur médiane mesurée : 5 %.',
  INDICATIVE: 'Même calcul qu’une cote solide, mais sur peu de voitures identiques : le chiffre est juste en général (erreur médiane mesurée : 6 à 8 %), mais une seule annonce atypique peut le déplacer.',
  PERCENTILE: 'Pas assez de voitures identiques pour une cote. On classe donc ce prix parmi les voitures du même modèle en vente, à deux ans et quelques dizaines de milliers de kilomètres près. C’est un rang, pas une valeur : il ne tient pas compte de la finition ni de l’état.',
  ESTIMATION: 'Ni cote ni groupe assez grand : un modèle statistique estime le prix à partir de l’âge, du kilométrage et de l’énergie de milliers d’annonces du même modèle. Il se trompe en moyenne de 15 % ; la fourchette contient le juste prix 8 fois sur 10.',
};

const virgule = (n) => String(n).replace('.', ',');

export function nomGroupe(marque, modele, groupe) {
  const energie = groupe?.energie ? ` ${groupe.energie.toLowerCase()}` : '';
  const annees = groupe?.annee_min ? ` de ${groupe.annee_min} à ${groupe.annee_max}` : '';
  return `${[marque, modele].filter(Boolean).join(' ') || 'voitures'}${energie}${annees}`;
}

function Pourquoi({ niveau }) {
  if (!POURQUOI[niveau]) return null;
  return (
    <details className="zk-vp-pourquoi">
      <summary>pourquoi ?</summary>
      <p>{POURQUOI[niveau]}</p>
    </details>
  );
}

function Badge({ niveau, detail }) {
  return (
    <p className={`zk-vp-badge ${niveau.toLowerCase()}`}>
      <b>{LIBELLES_NIVEAUX[niveau]}</b>{detail ? <span> · {detail}</span> : null}
    </p>
  );
}

function Cote({ v }) {
  const indicative = v.niveau === 'INDICATIVE';
  const approx = indicative ? '≈ ' : '';
  const pct = v.ecart_pct ?? Math.round(((v.cote - v.prix) * 1000) / v.cote) / 10;
  const eur = v.ecart_eur ?? Math.round(v.cote - v.prix);
  const n = v.comparables;
  const detail = n ? `${n} voiture${n > 1 ? 's' : ''} identique${n > 1 ? 's' : ''} en vente` : null;
  let tuile;
  if (pct >= ZONE_MORTE_PCT) {
    tuile = (
      <div className={`zk-ecart-tuile${indicative ? ' zk-vp-pointilles' : ''}`}>
        {approx}−{nombre(eur)} € sous la cote
        <small>soit {virgule(pct)} % de moins que les voitures identiques</small>
      </div>
    );
  } else if (pct > -ZONE_MORTE_PCT) {
    tuile = (
      <div className={`zk-ecart-tuile juste${indicative ? ' zk-vp-pointilles' : ''}`}>
        Au prix du marché<small>à moins de 3 % de la cote</small>
      </div>
    );
  } else {
    tuile = (
      <div className={`zk-ecart-tuile dessus${indicative ? ' zk-vp-pointilles' : ''}`}>
        {approx}+{nombre(-eur)} € au-dessus de la cote<small>ce n’est pas une bonne affaire à ce prix</small>
      </div>
    );
  }
  return (
    <>
      <Badge niveau={v.niveau} detail={detail} />
      <p className="zk-ecart-cote">cote {approx}{pct >= ZONE_MORTE_PCT ? <s>{nombre(v.cote)} €</s> : `${nombre(v.cote)} €`}</p>
      {tuile}
    </>
  );
}

function Percentile({ v, marque, modele }) {
  const p = v.percentile;
  const phrase = p >= 50 ? `Moins chère que ${p} %` : `Plus chère que ${100 - p} %`;
  return (
    <>
      <Badge niveau="PERCENTILE" detail={`${v.groupe_taille} voitures comparées`} />
      <div className="zk-vp-rang">
        <p className="zk-vp-rang-phrase">{phrase}</p>
        <p className="zk-vp-rang-groupe">des {nomGroupe(marque, modele, v.groupe)} en vente</p>
        <div className="zk-vp-reglette" role="img" aria-label={`${phrase} des voitures comparées`}>
          <span className="zk-vp-reglette-repere" style={{ left: `${100 - p}%` }} />
        </div>
        <p className="zk-vp-reglette-bornes"><span>moins chères</span><span>plus chères</span></p>
      </div>
    </>
  );
}

function Estimation({ v }) {
  const { bas, haut, prix } = v;
  const etendue = Math.max(1, haut - bas);
  const position = Math.min(100, Math.max(0, ((prix - bas) / etendue) * 100));
  let lecture = 'Le prix demandé est dans la fourchette.';
  if (prix < bas) lecture = 'Le prix demandé est sous la fourchette : à vérifier de près.';
  if (prix > haut) lecture = 'Le prix demandé est au-dessus de la fourchette.';
  return (
    <>
      <Badge niveau="ESTIMATION" detail="pas une cote" />
      <div className="zk-vp-fourchette">
        <p className="zk-vp-fourchette-valeurs">Entre {nombre(bas)} € et {nombre(haut)} €</p>
        <div className="zk-vp-barre" role="img" aria-label={`Fourchette de ${nombre(bas)} à ${nombre(haut)} euros`}>
          <span className="zk-vp-barre-prix" style={{ left: `${position}%` }} />
        </div>
        <p className="zk-vp-fourchette-lecture">{lecture}</p>
      </div>
    </>
  );
}

export default function VerdictPrix({ v, marque, modele }) {
  const niveau = v?.niveau;
  if (!niveau) {
    return (
      <p className="zk-ecart-sansavis">
        {v?.explication || 'Pas assez de voitures comparables en vente pour situer ce prix. Plutôt qu’un chiffre inventé, nous préférons ne rien avancer.'}
      </p>
    );
  }
  return (
    <div className={`zk-vp zk-vp-${niveau.toLowerCase()}`}>
      {(niveau === 'SOLIDE' || niveau === 'INDICATIVE') && <Cote v={v} />}
      {niveau === 'PERCENTILE' && <Percentile v={v} marque={marque} modele={modele} />}
      {niveau === 'ESTIMATION' && <Estimation v={v} />}
      <Pourquoi niveau={niveau} />
    </div>
  );
}

/** Version carte : une tuile, même code visuel que le verdict complet. */
export function VerdictCompact({ v }) {
  const niveau = v?.niveau;
  if (!niveau) return null;
  if (niveau === 'SOLIDE' || niveau === 'INDICATIVE') {
    if (v.ecart_eur == null || v.ecart_eur <= 0) return null;
    const indicative = niveau === 'INDICATIVE';
    return (
      <div className={`zk-aff-ecart${indicative ? ' zk-vp-pointilles' : ''}`}>
        {indicative ? '≈ ' : ''}−{nombre(v.ecart_eur)} €<small>sous la cote{indicative ? ' indicative' : ''}</small>
      </div>
    );
  }
  if (niveau === 'PERCENTILE') {
    return (
      <div className="zk-aff-ecart zk-vp-compact-rang">
        {v.percentile >= 50 ? `Moins chère que ${v.percentile} %` : `Plus chère que ${100 - v.percentile} %`}
        <small>des voitures comparables</small>
      </div>
    );
  }
  return (
    <div className="zk-aff-ecart zk-vp-compact-estimation">
      {nombre(v.bas)} – {nombre(v.haut)} €<small>estimation indicative</small>
    </div>
  );
}
